import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
  TextField,
  Button,
  Paper,
  Slide,
  useTheme,
  useMediaQuery,
  Collapse,
  Backdrop
} from '@mui/material';
import {
  Feedback as FeedbackIcon,
  Close as CloseIcon,
  Send as SendIcon
} from '@mui/icons-material';

const ADMIN_EMAIL = 'kontakt@dobrynajemca.pl';

// Sanitize: usuwa HTML/JS, zostawia tylko bezpieczny tekst z polskimi znakami
const sanitize = (str) => {
  return str
    .replace(/[<>]/g, '')
    .replace(/javascript:/gi, '')
    .replace(/data:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .replace(/[^\w\s.,!?@\-_():;\n\u00C0-\u017E]/g, '')
    .trim()
    .slice(0, 1000);
};

const SUPABASE_URL = 'https://jjbyrtlbadzrzilkwceg.supabase.co';
const SUPABASE_ANON_KEY = 'sb_publishable_t00zfmIZ4BTJjIsqmqtrpg_7wBk732X';

const BugReportWidget = ({ userEmail, open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleClose = () => {
    onClose();
    setError('');
    setDescription('');
    setSent(false);
  };

  const handleSend = async () => {
    const clean = sanitize(description);
    if (clean.length < 5) {
      setError('Opisz problem (min. 5 znaków)');
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/send-bug-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          description: clean,
          userEmail: userEmail || null,
          userAgent: navigator.userAgent,
          url: window.location.href,
        }),
      });
      if (!res.ok) throw new Error('Błąd serwera');
      setSent(true);
      setDescription('');
      setTimeout(() => { handleClose(); }, 2000);
    } catch (e) {
      setError('Nie udało się wysłać. Spróbuj ponownie.');
    } finally {
      setSending(false);
    }
  };

  const formContent = (
    <Paper elevation={8} sx={{
      width: isMobile ? '100%' : 320,
      maxWidth: isMobile ? '100%' : 320,
      borderRadius: isMobile ? '16px 16px 0 0' : 3,
      overflow: 'hidden',
      border: '1px solid #e5e7eb'
    }}>
      <Box sx={{
        background: 'linear-gradient(135deg, #e03d3d, #ff5252)',
        px: 2,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FeedbackIcon sx={{ color: '#fff', fontSize: 18 }} />
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>
            Zgłoś problem
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: '#fff', p: 0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ p: 2 }}>
        <Typography sx={{ fontSize: '13px', color: '#4B5563', mb: 1.5 }}>
          {sent ? '✅ Zgłoszenie wysłane! Dziękujemy.' : 'Opisz co nie działa — wyślemy zgłoszenie bezpośrednio do nas.'}
        </Typography>
        <TextField
          multiline
          rows={4}
          fullWidth
          placeholder="Np. nie mogę dodać zdjęcia, przycisk nie reaguje..."
          value={description}
          onChange={(e) => {
            setDescription(e.target.value.slice(0, 1000));
            if (error) setError('');
          }}
          error={!!error}
          helperText={error || description.length + '/1000'}
          size="small"
          inputProps={{ maxLength: 1000 }}
          sx={{
            mb: 1.5,
            '& .MuiOutlinedInput-root': {
              fontSize: '13px',
              '&.Mui-focused fieldset': { borderColor: '#ff5252' }
            },
            '& .MuiFormHelperText-root': { textAlign: 'right', mr: 0 }
          }}
        />
        <Button
          fullWidth
          variant="contained"
          size="medium"
          disabled={description.trim().length < 5 || sending || sent}
          onClick={handleSend}
          startIcon={<SendIcon sx={{ fontSize: '16px' }} />}
          sx={{
            backgroundColor: sent ? '#16a34a' : '#ff5252',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '13px',
            '&:hover': { backgroundColor: sent ? '#15803d' : '#e03d3d' },
            '&.Mui-disabled': { backgroundColor: '#e5e7eb', color: '#9ca3af' }
          }}
        >
          {sending ? 'Wysyłanie...' : sent ? 'Wysłano!' : 'Wyślij zgłoszenie'}
        </Button>
      </Box>
    </Paper>
  );

  if (isMobile) {
    return (
      <>
        <Backdrop open={open} onClick={handleClose} sx={{ zIndex: 1299 }} />
        <Slide direction="up" in={open} mountOnEnter unmountOnExit>
          <Box sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1300,
          }}>
            {formContent}
          </Box>
        </Slide>
      </>
    );
  }

  return (
    <Box sx={{
      position: 'fixed',
      bottom: 24,
      right: 24,
      zIndex: 1300,
    }}>
      <Collapse in={open} unmountOnExit>
        {formContent}
      </Collapse>
    </Box>
  );
};

export default BugReportWidget;
